import { HttpError } from "../HttpClient";
import errorCodes from "./errorCodes.json";

/**
 * Extracts a user-friendly error message from an HttpError or generic error with response data.
 *
 * @param error - The error object (can be HttpError, error with response.data, or unknown)
 * @param defaultMessage - Optional default message to use if no errorCode is found
 * @returns A user-friendly error message in Portuguese
 *
 * @example
 * ```typescript
 * try {
 *   await someService.someMethod();
 * } catch (error) {
 *   const message = getErrorMessage(error, "Não foi possível executar a operação. Tente novamente.");
 *   throw new Error(message);
 * }
 * ```
 */
export function getErrorMessage(
  error: unknown,
  defaultMessage: string = "Não foi possível concluir a operação. Tente novamente. Se o problema continuar, contacte a equipa de Suporte.",
): string {
  let responseData: any = null;

  if (error instanceof HttpError && error.responseData) {
    responseData = error.responseData;
  } else if (
    error &&
    typeof error === "object" &&
    "response" in error &&
    error.response &&
    typeof error.response === "object" &&
    "data" in error.response
  ) {
    responseData = (error as { response: { data: any } }).response.data;
  }

  if (responseData) {
    const errorCode = responseData.errorCode;

    // Handle ERROR_VALIDATION_TRANSLATED special case
    if (errorCode === "ERROR_VALIDATION_TRANSLATED") {
      if (responseData.details && Array.isArray(responseData.details)) {
        const detailsArray = responseData.details.filter(
          (detail: any) => typeof detail === "string",
        );
        if (detailsArray.length > 0) {
          return detailsArray.join("\n");
        }
      }
      // Fallback to default message if details is not valid
      return defaultMessage;
    }

    if (
      typeof errorCode === "string" &&
      Object.prototype.hasOwnProperty.call(errorCodes, errorCode)
    ) {
      return (errorCodes as Record<string, string>)[errorCode];
    }
  }

  /* HttpClient's own timeout says more than any caller's generic message. */
  if (error instanceof HttpError && error.status === 408) {
    return error.message;
  }

  return defaultMessage;
}
